import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../../componentes/button/button";
import FilialService from "../../../../services/resource/filialService";
import TipoPagamentoService from "../../../../services/resource/tipoPagamentoService";
import LancamentoDespesaService from "../../../../services/resource/lancamentoDespesaService";
import SolicitacaoDespesaCampos, { validarCamposDespesa, montarRateioParaEnvio } from "./SolicitacaoDespesaCampos";

const filialService = new FilialService();
const tipoPagamentoService = new TipoPagamentoService();
const service = new LancamentoDespesaService();

function montarFormInicial(detalheProcesso) {
    return {
        filialId: detalheProcesso.filialId ?? null,
        fornecedorId: detalheProcesso.fornecedorId ?? null,
        descricao: detalheProcesso.descricao ?? "",
        tipoDespesa: detalheProcesso.tipoDespesa ?? "SUPRIMENTOS",
        valorTotal: detalheProcesso.valorTotal ?? "",
        vencimento: detalheProcesso.vencimento ?? "",
        tipoPagamento: detalheProcesso.tipoPagamento ?? "",
        tipoConta: detalheProcesso.tipoConta ?? "",
        agencia: detalheProcesso.agencia ?? "",
        agenciaDigito: detalheProcesso.agenciaDigito ?? "",
        conta: detalheProcesso.conta ?? "",
        contaDigito: detalheProcesso.contaDigito ?? "",
        chavePix: detalheProcesso.chavePix ?? "",
        rateio: (detalheProcesso.rateio || []).map(item => ({ ...item, valor: item.valorRateado })),
        recorrenciaOcorrenciaId: null,
    };
}

/**
 * Etapa 1 revisitada após um RETIFICAR (ver EtapaDespesaAprovacaoHandler no backend) — ao
 * contrário de EtapaSolicitacaoRetificada.jsx (placeholder genérico usado pelos demais
 * processos), aqui os campos originais ficam de fato editáveis, pré-preenchidos com os dados
 * atuais da solicitação (detalheProcesso, já carregado pela tela — sem chamada extra). Mesmos
 * campos/validação da criação (SolicitacaoDespesaCampos), só muda o destino do envio: edita a
 * solicitação existente (PUT) em vez de criar uma nova (POST).
 */
export default function EtapaDespesaSolicitacaoRetificada({ solicitacao, detalheProcesso, acaoEmExecucao, onAcaoConcluida, encaminhar, anexoObrigatorioNaEtapa, temAnexo }) {
    // `detalheProcesso` chega null no primeiro render da tela de detalhe (ela busca a solicitação
    // e só depois o detalhe do processo, em duas etapas) — não dá pra inicializar o form a partir
    // dele direto no useState (só rodaria uma vez, com null). Preenche via efeito assim que chega.
    const [form, setForm] = useState(null);
    const [fornecedorInicial, setFornecedorInicial] = useState(null);
    const [filiais, setFiliais] = useState([]);
    const [tiposPagamento, setTiposPagamento] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [pendentesRecebimento, setPendentesRecebimento] = useState([]);

    useEffect(() => {
        filialService.listarAtivas().then(res => setFiliais(res.data)).catch(() => setFiliais([]));
        tipoPagamentoService.listar().then(res => setTiposPagamento(res.data)).catch(() => setTiposPagamento([]));
    }, []);

    useEffect(() => {
        if (!detalheProcesso) return;
        setForm(montarFormInicial(detalheProcesso));
        setFornecedorInicial(detalheProcesso.fornecedorId
            ? { id: detalheProcesso.fornecedorId, nome: detalheProcesso.fornecedorNome, cnpj: detalheProcesso.fornecedorCnpj }
            : null);
    }, [detalheProcesso]);

    async function reenviar() {
        const erro = validarCamposDespesa({ form });
        if (erro) {
            toast.error(erro);
            return;
        }
        if (pendentesRecebimento.length > 0) {
            toast.error("Esta nota deve ser lançada pelo processo de Recebimento de Compras — resolva a compra pendente ou troque o fornecedor.");
            return;
        }
        if (anexoObrigatorioNaEtapa && !temAnexo) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return;
        }

        // Diferente da criação (onde `encaminhar` espera o código da ETAPA DESTINO, já que a
        // solicitação ainda não existe), aqui — dentro da tela de detalhe — `encaminhar` espera o
        // código da AÇÃO válida na etapa atual (ETAPA_1_SOLICITACAO só permite "ENVIAR").
        let usuarioAprovadorId;
        try {
            usuarioAprovadorId = await encaminhar("ENVIAR");
        } catch {
            return;
        }

        setEnviando(true);
        service.editarSolicitacao(solicitacao.id, {
            ...form,
            usuarioAprovadorId,
            valorTotal: Number(form.valorTotal),
            rateio: montarRateioParaEnvio(form),
        })
            .then(res => onAcaoConcluida("ENVIAR", res.data))
            .catch(error => console.error("Erro ao reenviar solicitação", error))
            .finally(() => setEnviando(false));
    }

    if (!form) {
        return (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <i className="fa fa-spinner fa-spin mr-2" /> Carregando dados da solicitação...
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
                Esta solicitação foi retificada e voltou para a etapa inicial. Ajuste os campos abaixo
                conforme solicitado e reenvie.
            </p>

            <SolicitacaoDespesaCampos
                form={form}
                setForm={setForm}
                filiais={filiais}
                tiposPagamento={tiposPagamento}
                fornecedorInicial={fornecedorInicial}
                onPendenciaRecebimentoChange={setPendentesRecebimento}
            />

            <div className="flex justify-end">
                <Button
                    variant="blue"
                    label="Reenviar"
                    iconLeft="fa fa-paper-plane"
                    loading={enviando || acaoEmExecucao === "ENVIAR"}
                    disabled={pendentesRecebimento.length > 0}
                    onClick={reenviar}
                />
            </div>
        </div>
    );
}
